import { supabase } from '../lib/supabase';

export interface OnboardingProgress {
  has_visited_health_tree: boolean;
  has_created_lock_in: boolean;
  has_reported_progress: boolean;
  tour_completed: boolean;
  last_updated: string;
}

class OnboardingService {
  
  /**
   * Migrate localStorage tutorial data to database (one-time migration)
   */
  private async migrateLocalStorageToDatabase(userId: string): Promise<void> {
    try {
      const localTourCompleted = localStorage.getItem(`tour_completed_${userId}`) === 'true';
      const localHealthTreeVisited = localStorage.getItem(`health_tree_visited_${userId}`) === 'true';

      if (localTourCompleted || localHealthTreeVisited) {
        const updates: any = {};
        
        if (localTourCompleted) {
          updates.tutorial_completed = true;
          updates.tutorial_completed_at = new Date().toISOString();
        }
        
        if (localHealthTreeVisited) {
          updates.health_tree_visited = true;
          updates.health_tree_visited_at = new Date().toISOString();
        }

        const { error } = await supabase
          .from('users')
          .update(updates)
          .eq('id', userId);

        if (!error) {
          // Clear localStorage after successful migration
          localStorage.removeItem(`tour_completed_${userId}`);
          localStorage.removeItem(`health_tree_visited_${userId}`);
          localStorage.removeItem(`tour_active_${userId}`);
          console.log('Migrated tutorial data from localStorage to database');
        }
      }
    } catch (error) {
      console.warn('Error migrating localStorage tutorial data:', error);
    }
  }

  /**
   * Get user's onboarding progress
   */
  async getOnboardingProgress(userId: string): Promise<OnboardingProgress> {
    try {
      // First, attempt to migrate any existing localStorage data
      await this.migrateLocalStorageToDatabase(userId);

      // Get user's tutorial progress from database
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('tutorial_completed, health_tree_visited')
        .eq('id', userId)
        .single();

      if (userError) {
        console.warn('Error fetching user tutorial progress:', userError);
      }

      const hasVisitedHealthTree = user?.health_tree_visited || false;
      
      // Check if user has created a lock-in
      const { data: lockings, error: lockingError } = await supabase
        .from('weekly_lockings')
        .select('id')
        .eq('user_id', userId)
        .limit(1);
      
      if (lockingError) {
        console.warn('Error checking lock-in status:', lockingError);
      }
      
      const hasCreatedLockIn = lockings && lockings.length > 0;
      
      // Check if user has reported progress through multiple indicators
      let hasReportedProgress = false;
      
      // 1. Check for progress entries tied to lock-ins (original check)
      const { data: progress, error: progressError } = await supabase
        .from('progress_entries')
        .select('id')
        .eq('locking_id', lockings?.[0]?.id || '')
        .limit(1);
      
      if (progressError) {
        console.warn('Error checking progress status:', progressError);
      }
      
      if (progress && progress.length > 0) {
        hasReportedProgress = true;
      }
      
      // 2. Check for health worth assessments (also indicates progress reporting)
      if (!hasReportedProgress) {
        const { data: assessments, error: assessmentError } = await supabase
          .from('health_worth_assessments')
          .select('id')
          .eq('user_id', userId)
          .limit(1);
        
        if (assessmentError) {
          console.warn('Error checking health assessments:', assessmentError);
        }
        
        if (assessments && assessments.length > 0) {
          hasReportedProgress = true;
        }
      }
      
      // 3. Check for goal progress (goals with progress > 0)
      if (!hasReportedProgress) {
        const { data: goals, error: goalError } = await supabase
          .from('goals')
          .select('id')
          .eq('user_id', userId)
          .gt('progress', 0)
          .limit(1);
        
        if (goalError) {
          console.warn('Error checking goal progress:', goalError);
        }
        
        if (goals && goals.length > 0) {
          hasReportedProgress = true;
        }
      }
      
      // 4. Check for recent activity (last_activity_date within last 7 days)
      if (!hasReportedProgress) {
        const { data: userActivity, error: activityError } = await supabase
          .from('users')
          .select('last_activity_date')
          .eq('id', userId)
          .single();
        
        if (activityError) {
          console.warn('Error checking user activity:', activityError);
        }
        
        if (userActivity?.last_activity_date) {
          const lastActivity = new Date(userActivity.last_activity_date);
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          
          if (lastActivity > sevenDaysAgo) {
            hasReportedProgress = true;
          }
        }
      }
      
      // Check if tour is completed (from database)
      const tourCompleted = user?.tutorial_completed || false;
      
      return {
        has_visited_health_tree: hasVisitedHealthTree,
        has_created_lock_in: !!hasCreatedLockIn,
        has_reported_progress: hasReportedProgress,
        tour_completed: tourCompleted,
        last_updated: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Error getting onboarding progress:', error);
      return {
        has_visited_health_tree: false,
        has_created_lock_in: false,
        has_reported_progress: false,
        tour_completed: false,
        last_updated: new Date().toISOString(),
      };
    }
  }
  
  /**
   * Mark that user has visited the health tree
   */
  async markHealthTreeVisited(userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('users')
        .update({ 
          health_tree_visited: true,
          health_tree_visited_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('Error marking health tree as visited:', error);
        // Fallback to localStorage
        localStorage.setItem(`health_tree_visited_${userId}`, 'true');
      } else {
        console.log('Marked health tree as visited for user:', userId);
      }
    } catch (error) {
      console.error('Error updating health tree visit status:', error);
      // Fallback to localStorage
      localStorage.setItem(`health_tree_visited_${userId}`, 'true');
    }
  }
  
  /**
   * Mark that user has completed the tour
   */
  async markTourCompleted(userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('users')
        .update({ 
          tutorial_completed: true,
          tutorial_completed_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('Error marking tour as completed:', error);
        // Fallback to localStorage
        localStorage.setItem(`tour_completed_${userId}`, 'true');
      } else {
        console.log('Marked tour as completed for user:', userId);
        // Also clear localStorage to ensure consistency
        localStorage.removeItem(`tour_completed_${userId}`);
        localStorage.removeItem(`tour_active_${userId}`);
        localStorage.removeItem(`health_tree_visited_${userId}`);
      }
    } catch (error) {
      console.error('Error updating tour completion status:', error);
      // Fallback to localStorage
      localStorage.setItem(`tour_completed_${userId}`, 'true');
    }
  }
  
  /**
   * Mark that the tour is currently active (user is in the middle of taking it)
   */
  markTourActive(userId: string): void {
    localStorage.setItem(`tour_active_${userId}`, 'true');
  }
  
  /**
   * Clear tour active state (when tour is completed or explicitly closed)
   */
  clearTourActive(userId: string): void {
    localStorage.removeItem(`tour_active_${userId}`);
  }
  
  /**
   * Check if tour is currently active
   */
  isTourActive(userId: string): boolean {
    return localStorage.getItem(`tour_active_${userId}`) === 'true';
  }
  
  /**
   * Check if user should see the onboarding tour
   */
  async shouldShowTour(userId: string): Promise<boolean> {
    try {
      const progress = await this.getOnboardingProgress(userId);
      
      // Show tour if:
      // 1. Tour is currently active (user is in the middle of taking it), OR
      // 2. Tour hasn't been completed AND user hasn't done ALL main actions naturally
      if (this.isTourActive(userId)) {
        return true;
      }
      
      const hasCompletedAllSteps = progress.has_visited_health_tree && 
                                   progress.has_created_lock_in && 
                                   progress.has_reported_progress;
      
      return !progress.tour_completed && !hasCompletedAllSteps;
    } catch (error) {
      console.error('Error checking if should show tour:', error);
      return false;
    }
  }
  
  /**
   * Check if user should see the progress widget
   */
  async shouldShowProgressWidget(userId: string): Promise<boolean> {
    try {
      const progress = await this.getOnboardingProgress(userId);
      
      // Show widget if not all steps are completed
      return !(progress.has_visited_health_tree && 
               progress.has_created_lock_in && 
               progress.has_reported_progress);
    } catch (error) {
      console.error('Error checking if should show progress widget:', error);
      return true; // Default to showing it
    }
  }
  
  /**
   * Get completion percentage for progress tracking
   */
  async getCompletionPercentage(userId: string): Promise<number> {
    try {
      const progress = await this.getOnboardingProgress(userId);
      
      const steps = [
        progress.has_visited_health_tree,
        progress.has_created_lock_in,
        progress.has_reported_progress,
      ];
      
      const completedSteps = steps.filter(Boolean).length;
      return (completedSteps / steps.length) * 100;
    } catch (error) {
      console.error('Error calculating completion percentage:', error);
      return 0;
    }
  }
  
  /**
   * Reset onboarding progress (for testing)
   */
  async resetProgress(userId: string): Promise<void> {
    try {
      // Reset database fields
      const { error } = await supabase
        .from('users')
        .update({ 
          tutorial_completed: false,
          tutorial_completed_at: null,
          health_tree_visited: false,
          health_tree_visited_at: null
        })
        .eq('id', userId);

      if (error) {
        console.error('Error resetting tutorial progress in database:', error);
      }

      // Clear localStorage
      localStorage.removeItem(`health_tree_visited_${userId}`);
      localStorage.removeItem(`tour_completed_${userId}`);
      localStorage.removeItem(`tour_active_${userId}`);
      
      console.log('Reset onboarding progress for user:', userId);
    } catch (error) {
      console.error('Error resetting onboarding progress:', error);
      // Still clear localStorage even if database update fails
      localStorage.removeItem(`health_tree_visited_${userId}`);
      localStorage.removeItem(`tour_completed_${userId}`);
      localStorage.removeItem(`tour_active_${userId}`);
    }
  }
}

export const onboardingService = new OnboardingService();